/**
 * 日期工具函数模块 - 提供基础的日期格式化功能
 *
 * 此模块专注于简单的日期处理，复杂的时区转换请使用timeUtils.js和timezoneUtils.js
 *
 * 重要说明：
 * - 不设置全局 dayjs.locale，以避免影响其他需要英文输出的模块（如邮件编辑器）
 * - 如需中文格式化，请使用实例级 .locale("zh-cn") 调用
 * - 例如：dayjs().locale("zh-cn").fromNow() 而非全局设置后直接调用 dayjs().fromNow()
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn"; // 预加载中文 locale，但不全局设置

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
// 注意：不再调用 dayjs.locale("zh-cn")，避免污染全局 locale
// 如需中文输出，请使用 dayjs().locale("zh-cn").format(...) 实例级调用


/**
 * 常用日期格式
 */
export const DATE_FORMATS = {
    DATE: "YYYY-MM-DD",
    TIME: "HH:mm",
    DATE_TIME: "YYYY-MM-DD HH:mm",
    DATE_TIME_SECONDS: "YYYY-MM-DD HH:mm:ss",
    ISO_DATE_TIME: "YYYY-MM-DDTHH:mm:ssZ",
    SHORT_DATE: "MM/DD",
    DISPLAY_DATE: "YYYY年MM月DD日",
    MONTH_DAY: "MM-DD",
};

/**
 * 无效/空日期的统一占位符。
 * 仅用于展示型格式化函数（formatDate/formatDateTime/formatTime），
 * 序列化与字符串拼接类函数（toISOString、formatTimeToHHMM 等）仍返回空串。
 */
export const INVALID_DATE_PLACEHOLDER = "—";

/**
 * 格式化日期时间
 * @param {string|Date} date - 日期对象或字符串
 * @param {string} format - 输出格式，默认为 YYYY-MM-DD HH:mm
 * @param {boolean} useUTC - 是否使用UTC时间，默认为false
 * @returns {string} 格式化后的日期字符串；入参为 null/undefined/空串/不可解析值时返回 INVALID_DATE_PLACEHOLDER
 */
export function formatDateTime(date, format = DATE_FORMATS.DATE_TIME, useUTC = false) {
    if (!date) return INVALID_DATE_PLACEHOLDER;

    try {
        const dateObj = useUTC ? dayjs.utc(date) : dayjs(date);
        if (!dateObj.isValid()) {
            console.warn("无效日期:", date);
            return INVALID_DATE_PLACEHOLDER;
        }
        return dateObj.format(format);
    } catch (error) {
        console.error("日期格式化错误:", error);
        return INVALID_DATE_PLACEHOLDER;
    }
}

/**
 * 格式化日期（不含时间）
 * @param {string|Date} date - 日期对象或字符串
 * @param {string} format - 输出格式，默认为 YYYY-MM-DD
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = DATE_FORMATS.DATE) {
    return formatDateTime(date, format);
}

/**
 * 格式化时间（不含日期）
 * @param {string|Date} date - 日期对象或字符串
 * @param {string} format - 输出格式，默认为 HH:mm
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime(date, format = DATE_FORMATS.TIME) {
    return formatDateTime(date, format);
}

/**
 * 格式化为相对时间（如"3小时前"）
 * 使用实例级中文 locale，不影响全局状态
 * @param {string|Date} date - 日期对象或字符串
 * @returns {string} 相对时间字符串（中文）
 */
export function formatRelativeTime(date) {
    if (!date) return "";

    try {
        const dateObj = dayjs(date);
        if (!dateObj.isValid()) return "";
        // 使用实例级 locale 调用，保证中文输出且不污染全局状态
        return dateObj.locale("zh-cn").fromNow();
    } catch (error) {
        console.error("相对时间格式化错误:", error);
        return "";
    }
}

/**
 * 检查日期是否有效
 * @param {string|Date} date - 日期对象或字符串
 * @returns {boolean} 是否有效
 */
export function isValidDate(date) {
    if (!date) return false;
    return dayjs(date).isValid();
}

/**
 * 获取两个日期之间的天数差
 * @param {string|Date} startDate - 开始日期
 * @param {string|Date} endDate - 结束日期
 * @returns {number} 天数差
 */
export function getDaysDifference(startDate, endDate) {
    if (!startDate || !endDate) return 0;

    try {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (!start.isValid() || !end.isValid()) return 0;
        return end.diff(start, "day");
    } catch (error) {
        console.error("计算天数差错误:", error);
        return 0;
    }
}

/**
 * 转换为ISO8601格式（带Z后缀的UTC时间）
 * @param {string|Date} date - 日期对象或字符串
 * @returns {string} ISO8601格式的时间字符串
 */
export function toISOString(date) {
    if (!date) return "";

    try {
        const dateObj = dayjs(date);
        if (!dateObj.isValid()) return "";
        return dateObj.toISOString();
    } catch (error) {
        console.error("ISO格式转换错误:", error);
        return "";
    }
}

/**
 * 格式化时间显示为HH:mm格式
 * @param {string} timeString - 时间字符串或日期时间字符串
 * @returns {string} HH:mm格式的时间
 */
export function formatTimeToHHMM(timeString) {
    if (!timeString) return "";

    try {
        // 如果是ISO格式带T的字符串，提取时间部分
        if (typeof timeString === "string" && timeString.includes("T")) {
            const parts = timeString.split("T");
            if (parts.length > 1) {
                timeString = parts[1].substring(0, 5); // 提取HH:mm部分
                return timeString;
            }
        }

        // 否则尝试用dayjs解析
        const time = dayjs(timeString);
        if (time.isValid()) {
            return time.format("HH:mm");
        }

        return timeString;
    } catch (error) {
        console.error("时间格式化错误:", error);
        return timeString;
    }
}

/**
 * 将分钟数转换为HH:mm格式
 * @param {number} minutes - 总分钟数
 * @returns {string} 格式化后的HH:mm时间
 */
export function formatMinutesToHHMM(minutes) {
    if (minutes === undefined || minutes === null) return "";

    try {
        const hours = Math.floor(Math.abs(minutes) / 60);
        const mins = Math.abs(minutes) % 60;

        return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    } catch (error) {
        console.error("分钟转换为HH:MM格式错误:", error);
        return "";
    }
}
